import { useNavigate, useLocation } from "react-router-dom";
import { useContext, useState, useEffect, useMemo } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { User, Pencil, Save, RotateCcw, ArrowLeft } from "lucide-react";

const SupplierContactAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor } = currentSkin;

  // ✅ FIX: supplierId now comes from navigation state instead of being
  // hardcoded to 1, same pattern as SupplierBankAdd.jsx.
  const supplier = location.state?.supplier || null;
  const supplierId =
    supplier?.fld_suppliermainid ||
    supplier?.fld_supplierid ||
    location.state?.supplierid ||
    "";

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdHeader = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdHeader,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdHeader, branchType, userId]
  );

  const [contactName, setContactName] = useState("");
  const [designation, setDesignation] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [remarks, setRemarks] = useState("");
  const [editId, setEditId] = useState(null);

  const [tableData, setTableData] = useState([]);
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const resetForm = () => {
    setContactName("");
    setDesignation("");
    setPhone("");
    setMobile("");
    setEmail("");
    setRemarks("");
    setEditId(null);
    setFormError("");
  };

  const handleSubmit = async () => {
    setFormError("");

    if (!supplierId) {
      setFormError("Supplier is missing — please reopen this page from the supplier edit screen.");
      return;
    }
    if (!contactName) {
      setFormError("Contact Name is required.");
      return;
    }

    const payload = {
      contactname: contactName,
      designation: designation,
      phoneno: phone,
      mobile: mobile,
      email: email,
      remarks: remarks,
    };

    let url = "";
    if (editId) {
      payload.contactid = editId;
      url = "https://nissi-india.com/api/updateSupplierContactDetail";
    } else {
      // ✅ FIX: was hardcoded to supplierid: 1 — every contact was
      // attached to the same supplier regardless of which one you
      // were actually editing.
      payload.supplierid = supplierId;
      url = "https://nissi-india.com/api/addSupplierContactDetail";
    }

    setLoading(true);
    try {
      const res = editId
        ? await axios.put(url, payload, { headers: commonHeaders })
        : await axios.post(url, payload, { headers: commonHeaders });
      console.log("Server Response:", res.data);

      const isSuccess =
        res.data?.success === true ||
        res.data?.status === "ok" ||
        res.data?.message?.toLowerCase()?.includes("success");

      if (isSuccess) {
        toast.success(`Contact ${editId ? "updated" : "added"} successfully`);
        resetForm();
        fetchTableData();
      } else {
        setFormError("Submission failed.");
      }
    } catch (err) {
      console.error("Error submitting contact details:", err);
      const serverMsg = err.response?.data?.message || err.response?.data?.error;
      setFormError(serverMsg || "Error submitting data.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIX: was calling /getSupplierContactDetailById?contactid=7 — a
  // single hardcoded record. Now calls /bindSupplierContact?supplierid=...
  // (the same list endpoint SupplierMainMasEdit.jsx uses for its Contact
  // tab) to fetch every contact belonging to the current supplier.
  const fetchTableData = async () => {
    if (!supplierId) {
      setTableData([]);
      return;
    }
    setTableLoading(true);
    try {
      const res = await axios.get("https://nissi-india.com/api/bindSupplierContact", {
        params: { supplierid: supplierId },
        headers: commonHeaders,
      });
      setTableData(res.data?.contactList || []);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Error fetching table data");
      setTableData([]);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (!supplierId) {
      toast.error("Supplier not found. Please open this page from the supplier edit screen.");
      return;
    }
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

const handleEdit = (item) => {
  setEditId(item.fld_contactid);
  // ✅ FIX: same ContactName/Designation aliasing issue as the table —
  // without the fallback, clicking Edit would load a blank name/
  // designation into the form even though the record has real data.
  setContactName(item.ContactName || item.fld_contactname || "");
  setDesignation(item.Designation || item.fld_designation || "");
  setPhone(item.fld_phoneno || "");
  setMobile(item.fld_mobile || "");
  setEmail(item.fld_email || "");
  setRemarks(item.fld_remarks || "");
  setFormError("");
};

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Supplier Contact Details</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header + Back */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Contact Details</h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Manage supplier contact persons
              </p>
            </div>
          </div>

          <button
  onClick={() =>
    navigate("/inventory/Supplier-Main-MasEdit", { state: { supplierid: supplierId } })
  }
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
              isDark
                ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                : "border-slate-300 text-slate-700 hover:bg-slate-100"
            }`}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>

        {/* Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div
            className={`px-6 py-4 border-b flex items-center gap-3 ${
              isDark ? "border-slate-700/50 bg-slate-900/40" : "border-slate-200/50 bg-white/60"
            }`}
          >
            <div className={`w-3 h-8 rounded-full bg-${baseColor}-500`}></div>
            <div>
              <h2 className="text-lg font-bold">
                {editId ? "Edit Contact Detail" : "Add Contact Detail"}
              </h2>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {editId
                  ? "Update the selected contact person"
                  : "Enter the supplier's contact person details"}
              </p>
            </div>
          </div>

          <div className="p-6">
            {formError && (
              <div
                className={`flex items-center justify-between gap-3 mb-4 px-4 py-2.5 rounded-lg border text-sm font-medium ${
                  isDark
                    ? "bg-red-900/30 border-red-500/40 text-red-300"
                    : "bg-red-50 border-red-200 text-red-700"
                }`}
              >
                <span>{formError}</span>
                <button
                  type="button"
                  onClick={() => setFormError("")}
                  className="shrink-0 opacity-70 hover:opacity-100"
                  aria-label="Dismiss error"
                >
                  ✕
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>
                    Contact Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className={inputClass}
                    placeholder="Enter contact name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Phone No</label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={inputClass}
                    placeholder="Enter phone number"
                  />
                </div>

                <div>
                  <label className={labelClass}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Designation</label>
                  <input
                    type="text"
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    className={inputClass}
                    placeholder="Enter designation"
                  />
                </div>

                <div>
                  <label className={labelClass}>Mobile</label>
                  <input
                    type="text"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    className={inputClass}
                    placeholder="Enter mobile number"
                  />
                </div>

                <div>
                  <label className={labelClass}>Remarks</label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={3}
                    className={`${inputClass} resize-none`}
                    placeholder="Optional notes..."
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3 mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
              {editId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <RotateCcw size={16} />
                  Cancel Edit
                </button>
              )}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
              >
                <Save size={16} />
                {loading ? "Saving..." : editId ? "Update Contact Detail" : "Save Contact Detail"}
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-500`}></div>
              <h3 className="text-xl font-bold">Contact Details List</h3>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Contact Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Designation</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Select</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Edit</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {tableLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-400">
                      Loading...
                    </td>
                  </tr>
                ) : tableData.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-gray-500">
                      No contact details found
                    </td>
                  </tr>
                ) : (
                  tableData.map((item, index) => (
                    <tr
                      key={item.fld_contactid || index}
                      className={`transition ${
                        isDark ? "bg-gray-800/20 hover:bg-gray-700/30" : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3">{index + 1}</td>
                      <td className="px-4 py-3 font-medium">{item.ContactName || item.fld_contactname}</td>
<td className="px-4 py-3">{item.Designation || item.fld_designation}</td>
                      <td className="px-4 py-3 text-center">
                        <input type="checkbox" className="w-4 h-4 rounded" />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEdit(item)}
                          className={`p-1.5 rounded-full transition ${
                            isDark
                              ? "bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50"
                              : "bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                          }`}
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default SupplierContactAdd;